import * as Sentry from '@sentry/react';
import * as Logging from '../actions/log';
import * as Selectors from '../selectors';
import { useSelector, useDispatch } from '../redux';

/**
 * This interface encourages further information
 * to be provided with every StellaError dispatched.
 *
 * The middleware takes the context and passes it
 * to Sentry to display in its UI, either via tags or the message string.
 *
 * type: error type, to be used as a tag
 * location: where the error happened, to be used as a tag
 * additionalTags: can be provided if the developer thinks they will be useful, e.g. GroupingCode.
 * additionalContext: more properties be provided here and will be appended to the message displayed in the Sentry UI.
 *
 */
export interface ErrorContext {
    // TODO: Look into replacing 'log' with a different, more semantic type. This type is meant to represent 'recoverable' errors. Currently, this causes confusion around 'logging errors', 'errors of type log', and so on within our services.
    type: 'model' | 'render' | 'log';
    location?: string;
    additionalTags?: { [key: string]: string };
    additionalContext?: { [key: string]: string | {} | [] };
}

const isLocalhost = () =>
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('192.168.1.');

export const useInitialiseSentry = () => {
    const sentryDsn = useSelector(Selectors.Core.sentryDsn);
    const environment = useSelector(Selectors.Core.environment);

    const dispatch = useDispatch();
    if (!isLocalhost()) {
        if (sentryDsn && environment) {
            Sentry.init({
                dsn: sentryDsn,
                environment: environment,
                // Disable Session Replay
                replaysSessionSampleRate: 0,
                replaysOnErrorSampleRate: 0,
            });
        } else {
            dispatch(
                Logging.logWarning('Sentry failed to initialise, missing DSN or environment.'),
            );
        }
    }
};
